var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { property } from 'lit/decorators.js';
import { $markLoaded, $needsRender, $onModelLoad, $progressTracker, $scene, $shouldAttemptPreload, $updateSource, $updateStatus, } from '../model-viewer-base.js';
import { $updateEnvironment } from './environment.js';
import { isPdfUrl } from '../utilities/content-type-detector.js';
import { clamp } from '../utilities.js';
const $pdfObject = Symbol('pdfObject');
const $pageControls = Symbol('pageControls');
const $onPrevPageClick = Symbol('onPrevPageClick');
const $onNextPageClick = Symbol('onNextPageClick');
const $updatePageControls = Symbol('updatePageControls');
const $createPageControls = Symbol('createPageControls');
const prevIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
</svg>`;
const nextIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
</svg>`;
/**
 * PDFMixin adds support for loading and displaying PDF files in model-viewer.
 * PDF files are automatically detected by file extension.
 */
export const PDFMixin = (ModelViewerElement) => {
    var _a, _b, _c, _d;
    class PDFModelViewerElement extends ModelViewerElement {
        constructor() {
            super(...arguments);
            this.pdfCurrentPage = 1;
            this.pdfTotalPages = 0;
            this[_a] = null;
            this[_b] = null;
            this[_c] = async () => {
                const pdfObject = this[$pdfObject];
                if (!pdfObject)
                    return;
                await pdfObject.previousPage();
                this.pdfCurrentPage = pdfObject.currentPage;
                this[$updatePageControls]();
                this[$needsRender]();
            };
            this[_d] = async () => {
                const pdfObject = this[$pdfObject];
                if (!pdfObject)
                    return;
                await pdfObject.nextPage();
                this.pdfCurrentPage = pdfObject.currentPage;
                this[$updatePageControls]();
                this[$needsRender]();
            };
        }
        connectedCallback() {
            super.connectedCallback();
            this[$updatePageControls]();
        }
        disconnectedCallback() {
            super.disconnectedCallback();
            if (this[$pageControls]) {
                this[$pageControls].remove();
                this[$pageControls] = null;
            }
        }
        updated(changedProperties) {
            super.updated(changedProperties);
            // Update PDF page if it changed externally
            if (this[$pdfObject] && changedProperties.has('pdfCurrentPage')) {
                this[$pdfObject].goToPage(this.pdfCurrentPage).catch(console.error);
                this[$needsRender]();
            }
        }
        [(_a = $pdfObject, _b = $pageControls, _c = $onPrevPageClick, _d = $onNextPageClick, $updatePageControls)]() {
            const pdfObject = this[$pdfObject];
            if (!pdfObject || pdfObject.totalPages <= 1) {
                if (this[$pageControls]) {
                    this[$pageControls].style.display = 'none';
                }
                return;
            }
            if (!this[$pageControls]) {
                this[$createPageControls]();
            }
            const controls = this[$pageControls];
            controls.style.display = 'flex';
            // Update page info
            const pageInfo = controls.querySelector('.pdf-page-info');
            if (pageInfo) {
                pageInfo.textContent = `${pdfObject.currentPage} / ${pdfObject.totalPages}`;
            }
            // Update button states
            const prevButton = controls.querySelector('.pdf-prev-button');
            const nextButton = controls.querySelector('.pdf-next-button');
            if (prevButton) {
                prevButton.disabled = pdfObject.currentPage <= 1;
            }
            if (nextButton) {
                nextButton.disabled = pdfObject.currentPage >= pdfObject.totalPages;
            }
        }
        [$createPageControls]() {
            const container = document.createElement('div');
            container.setAttribute('part', 'pdf-page-controls');
            container.className = 'pdf-page-controls';
            // Style the container
            Object.assign(container.style, {
                position: 'absolute',
                bottom: '20px',
                left: '50%',
                transform: 'translateX(-50%)',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: '12px',
                zIndex: '10',
                borderRadius: '4px',
                backdropFilter: 'blur(4px)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                padding: '8px 12px',
            });
            // Previous button
            const prevButton = document.createElement('button');
            prevButton.type = 'button';
            prevButton.className = 'pdf-prev-button';
            prevButton.setAttribute('aria-label', 'Previous page');
            prevButton.innerHTML = prevIcon;
            Object.assign(prevButton.style, {
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            });
            prevButton.addEventListener('click', this[$onPrevPageClick]);
            container.appendChild(prevButton);
            // Page info
            const pageInfo = document.createElement('div');
            pageInfo.className = 'pdf-page-info';
            Object.assign(pageInfo.style, {
                color: 'white',
                fontSize: '14px',
                fontWeight: '500',
                minWidth: '60px',
                textAlign: 'center',
            });
            pageInfo.textContent = '1 / 1';
            container.appendChild(pageInfo);
            // Next button
            const nextButton = document.createElement('button');
            nextButton.type = 'button';
            nextButton.className = 'pdf-next-button';
            nextButton.setAttribute('aria-label', 'Next page');
            nextButton.innerHTML = nextIcon;
            Object.assign(nextButton.style, {
                background: 'transparent',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                padding: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            });
            nextButton.addEventListener('click', this[$onNextPageClick]);
            container.appendChild(nextButton);
            // Add to shadow root
            const shadowRoot = this.shadowRoot;
            if (shadowRoot) {
                const containerElement = shadowRoot.querySelector('.container');
                if (containerElement) {
                    containerElement.appendChild(container);
                }
            }
            this[$pageControls] = container;
        }
        async [$updateSource]() {
            var _e;
            const scene = this[$scene];
            const source = this.src;
            // Check if the source is a PDF file
            const isPdf = source ? isPdfUrl(source) : false;
            if (isPdf) {
                // Handle PDF loading
                if (this.loaded || !this[$shouldAttemptPreload]() ||
                    source === scene.url) {
                    return;
                }
                if (this.generateSchema) {
                    scene.updateSchema(source);
                }
                this[$updateStatus]('Loading');
                const updateSourceProgress = this[$progressTracker].beginActivity('pdf-load');
                try {
                    const srcUpdated = scene.setPdfSource(source, (progress) => updateSourceProgress(clamp(progress, 0, 1) * 0.95));
                    const envUpdated = this[$updateEnvironment]();
                    await Promise.all([srcUpdated, envUpdated]);
                    this[$pdfObject] = (_e = scene.pdfObject) !== null && _e !== void 0 ? _e : null;
                    // Update page info
                    if (this[$pdfObject]) {
                        this.pdfTotalPages = this[$pdfObject].totalPages;
                        this.pdfCurrentPage = this[$pdfObject].currentPage;
                    }
                    this[$markLoaded]();
                    this[$onModelLoad]();
                    // Show page controls
                    this[$updatePageControls]();
                    this.updateComplete.then(() => {
                        this.dispatchEvent(new CustomEvent('before-render'));
                    });
                    // Wait for shaders to compile and pixels to be drawn.
                    await new Promise((resolve) => {
                        requestAnimationFrame(() => {
                            requestAnimationFrame(() => {
                                this.dispatchEvent(new CustomEvent('load', { detail: { url: source } }));
                                resolve();
                            });
                        });
                    });
                }
                catch (error) {
                    this[$pdfObject] = null;
                    this[$updatePageControls]();
                    this.dispatchEvent(new CustomEvent('error', {
                        detail: { type: 'loadfailure', sourceError: error },
                    }));
                }
                finally {
                    updateSourceProgress(1.0);
                }
            }
            else {
                // Fall back to default model loading
                this[$pdfObject] = null;
                this[$updatePageControls]();
                return super[$updateSource]();
            }
        }
    }
    __decorate([
        property({ type: Number, attribute: 'pdf-current-page' })
    ], PDFModelViewerElement.prototype, "pdfCurrentPage", void 0);
    __decorate([
        property({ type: Number, attribute: 'pdf-total-pages' })
    ], PDFModelViewerElement.prototype, "pdfTotalPages", void 0);
    return PDFModelViewerElement;
};
//# sourceMappingURL=pdf.js.map